/* ============================================================
   WORKSPACE v4 — PROTOTAIP DESIGN — pemilih Avatar (chip + menu)
   ============================================================
   Arahan user 18 Okt 2026: Avatar di ATAS SEKALI panel kanan, rupa
   ikut pemilih avatar Workspace sebenar — chip bermuka "No avatar ▾"
   yang membuka menu: No avatar · Default (M) · Default (F) | avatar
   simpanan (nama + jantina) | + New avatar  n/5.

   ⚠️ KEMBAR RUPA: `ChipMenu`, `Muka`, `BarisPop` + menu avatar dalam
      [PromptComposerV39.jsx] (Workspace sebenar). Disalin, BUKAN diimport,
      kerana fail itu sedang disunting sesi lain (WIP belum commit) dan
      komponennya tidak dieksport — menambah `export` di sana akan
      bercampur dengan kerja itu. Kelas CSS `s-chip`, `s-pop`, `s-pop__row`,
      `s-pop__sep`, `s-avthumb` DIPAKAI terus (satu sumber rupa). Bila
      prototaip ini disambung kelak, gabungkan kedua-dua salinan.

   PROTOTAIP: "New avatar" tidak membuat apa-apa (corak /workspace-v3.9);
   muka = huruf pertama, bukan gambar.
   ============================================================ */

import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, Pencil, Plus, UserX } from 'lucide-react';

function Muka({ av, kecil, pilih }) {
    const sisi = kecil ? 18 : 26;
    return (
        <span
            className="s-avthumb"
            style={{
                width: sisi, height: sisi, borderRadius: kecil ? 5 : 7, fontSize: kecil ? 10 : 11,
                borderStyle: av.tambah ? 'dashed' : 'solid',
                borderColor: pilih ? 'var(--s-select)' : undefined,
            }}
        >
            {av.tambah ? <Plus size={kecil ? 11 : 13} strokeWidth={2} />
                : av.kosong ? <UserX size={kecil ? 11 : 14} strokeWidth={1.8} />
                    : av.img ? <img src={av.img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }} />
                        : av.inisial}
        </span>
    );
}

function Baris({ tanda, nama, nota, dipilih, onClick, hujung }) {
    return (
        <div
            className="s-pop__row" role="button" tabIndex={0} onClick={onClick}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); } }}
        >
            {tanda}
            <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 13, color: 'var(--s-ink)' }}>{nama}</span>
                {nota && <span style={{ display: 'block', fontSize: 11.5, color: 'var(--s-ink-3)', marginTop: 1 }}>{nota}</span>}
            </span>
            {hujung && <span style={{ fontSize: 11.5, color: 'var(--s-ink-3)', flex: 'none' }}>{hujung}</span>}
            {dipilih && <Check size={14} strokeWidth={2.2} style={{ color: 'var(--s-ink)', flex: 'none' }} />}
        </div>
    );
}

/** Tutup menu bila klik di luar / Esc — dikongsi AvatarChip + ChipPilih. */
function useMenu() {
    const [buka, setBuka] = useState(false);
    const rujuk = useRef(null);
    useEffect(() => {
        if (!buka) return undefined;
        const luar = (e) => { if (rujuk.current && !rujuk.current.contains(e.target)) setBuka(false); };
        const esc = (e) => { if (e.key === 'Escape') setBuka(false); };
        document.addEventListener('mousedown', luar);
        document.addEventListener('keydown', esc);
        return () => {
            document.removeEventListener('mousedown', luar);
            document.removeEventListener('keydown', esc);
        };
    }, [buka]);
    return { buka, setBuka, rujuk };
}

/**
 * Chip dropdown umum (Scene, Model — arahan user 18 Okt: "avatar, scene, model dalam bentuk dropdown").
 * `pilihan` = [{ id, nama, nota? }]. Rupa SAMA dengan AvatarChip (s-chip + s-pop).
 */
export function ChipPilih({ ikon, label, pilihan, nilai, onPilih, lebar = 200, kanan = false }) {
    const { buka, setBuka, rujuk } = useMenu();
    return (
        <div ref={rujuk} style={{ position: 'relative', minWidth: 0 }}>
            <button
                type="button"
                className={`s-chip${buka ? ' s-chip--on' : ''}`}
                aria-haspopup="menu"
                aria-expanded={buka}
                onClick={() => setBuka((v) => !v)}
            >
                {ikon}
                <span>{label}</span>
                <ChevronDown size={13} strokeWidth={2} style={{ opacity: .6, flex: 'none' }} />
            </button>
            {buka && (
                <div className="s-pop" role="menu" style={kanan ? { width: lebar, left: 'auto', right: 0 } : { width: lebar }}>
                    {pilihan.map((x) => (
                        <Baris key={x.id} nama={x.nama} hujung={x.nota} dipilih={x.id === nilai}
                            onClick={() => { onPilih(x.id); setBuka(false); }} />
                    ))}
                </div>
            )}
        </div>
    );
}

/**
 * `avatar` = senarai contoh (No avatar, Default M/F, kemudian avatar simpanan `simpanan: true`).
 * `sorok` = style tanpa muka → chip diganti makluman (sama seperti `avatarMati` Workspace sebenar).
 * `baris` = rupa C4 "baris tetapan" (dipilih user 23 Okt): label Avatar · muka + nama · Change ▾,
 *   di ATAS kotak Prompt Input. Menu sama.
 */
export default function AvatarChip({ avatar, avatarId, onAvatar, sorok, slot = 5, baris = false, notaSorok, onBaharu, onSunting }) {
    const [buka, setBuka] = useState(false);
    const rujuk = useRef(null);

    useEffect(() => {
        if (!buka) return undefined;
        const luar = (e) => { if (rujuk.current && !rujuk.current.contains(e.target)) setBuka(false); };
        const esc = (e) => { if (e.key === 'Escape') setBuka(false); };
        document.addEventListener('mousedown', luar);
        document.addEventListener('keydown', esc);
        return () => {
            document.removeEventListener('mousedown', luar);
            document.removeEventListener('keydown', esc);
        };
    }, [buka]);

    if (sorok && baris) {
        return (
            <div className="gv2-avbaris" title="This style has no face on camera">
                <span className="gv2-avbaris__lbl">Avatar</span>
                <UserX size={15} strokeWidth={1.8} style={{ color: 'var(--s-ink-3)' }} />
                <span className="gv2-avbaris__nm" style={{ color: 'var(--s-ink-3)', fontWeight: 400 }}>{notaSorok || 'No face in this style'}</span>
            </div>
        );
    }
    if (sorok) {
        return (
            <span className="s-chip" style={{ cursor: 'default', color: 'var(--s-ink-3)' }} title="This style has no face on camera">
                <UserX size={14} strokeWidth={1.8} />
                <span>No face in this style</span>
            </span>
        );
    }

    const tetap = avatar.filter((a) => !a.simpanan);
    const simpanan = avatar.filter((a) => a.simpanan);
    const dipilih = avatar.find((a) => a.id === avatarId) || tetap[0];
    const pilih = (id) => { onAvatar(id); setBuka(false); };

    const menu = buka && (
        <div className="s-pop" role="menu" style={baris ? { width: 252, left: 'auto', right: 0 } : { width: 252 }}>
            {tetap.map((x) => (
                <Baris key={x.id || 'tiada'} tanda={<Muka av={x} pilih={x.id === dipilih.id} />} nama={x.nama}
                    dipilih={x.id === dipilih.id} onClick={() => pilih(x.id)} />
            ))}
            {simpanan.length > 0 && <span className="s-pop__sep" />}
            {simpanan.map((x) => (
                <Baris key={x.id} tanda={<Muka av={x} pilih={x.id === dipilih.id} />} nama={x.nama}
                    nota={x.gender} dipilih={x.id === dipilih.id} onClick={() => pilih(x.id)}
                    /* Sunting avatar (mod sebenar) — modal avatar SEDIA ADA di App.jsx. */
                    hujung={onSunting ? (
                        <span role="button" tabIndex={0} title="Edit avatar" style={{ display: 'inline-flex', padding: 2, cursor: 'pointer' }}
                            onClick={(e) => { e.stopPropagation(); setBuka(false); onSunting(x.id); }}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setBuka(false); onSunting(x.id); } }}>
                            <Pencil size={12} strokeWidth={2} />
                        </span>
                    ) : undefined} />
            ))}
            <span className="s-pop__sep" />
            <Baris
                tanda={<Muka av={{ tambah: true }} />}
                nama={<span style={{ color: 'var(--s-ink-2)' }}>New avatar</span>}
                hujung={`${simpanan.length}/${slot}`}
                onClick={() => { setBuka(false); onBaharu?.(); }}
            />
        </div>
    );

    if (baris) {
        return (
            <div ref={rujuk} className="gv2-avbaris">
                <span className="gv2-avbaris__lbl">Avatar</span>
                <Muka av={dipilih} />
                <span className="gv2-avbaris__nm">{dipilih.nama}{dipilih.gender && <span> · {dipilih.gender}</span>}</span>
                <button type="button" className="gv2-avbaris__tukar" aria-haspopup="menu" aria-expanded={buka} onClick={() => setBuka((v) => !v)}>
                    Change<ChevronDown size={13} strokeWidth={2} />
                </button>
                {menu}
            </div>
        );
    }

    return (
        <div ref={rujuk} style={{ position: 'relative', minWidth: 0 }}>
            <button
                type="button"
                className={`s-chip${buka ? ' s-chip--on' : ''}`}
                aria-haspopup="menu"
                aria-expanded={buka}
                onClick={() => setBuka((v) => !v)}
            >
                <Muka av={dipilih} kecil />
                <span>{dipilih.nama}</span>
                <ChevronDown size={13} strokeWidth={2} style={{ opacity: .6, flex: 'none' }} />
            </button>
            {menu}
        </div>
    );
}
